/**
 * Pseudopod/tentacle count tracker.
 *
 * The blob's consumption and (future) combat scale with tentacle count:
 * - Each tentacle = one concurrent consume slot (drives ConsumptionController.maxConcurrent)
 * - Future: each tentacle = an independent strike source (pseudopod-strike ability)
 * - Future: tentacle stacking on one prey could accelerate consume
 *   (not modeled yet — introduce when a tech-tree node requires it)
 *
 * Act 0 baseline: 1 tentacle. Tech-tree upgrades in Act 1+ raise this live
 * via setCount() in response to ability unlocks.
 */
export type TentacleListener = (count: number) => void;

export interface TentacleSystemOptions {
  initialCount?: number;
  min?: number;
  max?: number;
}

export class TentacleSystem {
  private _count: number;
  private readonly min: number;
  private readonly max: number;
  private listeners: TentacleListener[] = [];

  constructor(options: TentacleSystemOptions = {}) {
    this.min = Math.max(1, options.min ?? 1);
    this.max = options.max ?? 16;
    this._count = this.clamp(options.initialCount ?? this.min);
  }

  get count(): number {
    return this._count;
  }

  setCount(n: number): void {
    const next = this.clamp(n);
    if (next === this._count) return;
    this._count = next;
    for (const l of this.listeners) l(next);
  }

  add(delta: number): void {
    this.setCount(this._count + delta);
  }

  onChange(listener: TentacleListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private clamp(n: number): number {
    const floored = Math.floor(n);
    if (floored < this.min) return this.min;
    if (floored > this.max) return this.max;
    return floored;
  }
}
